// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { classicSkin } from './skins/classic';
import { clubhouseSkin } from './skins/clubhouse';
import { nightRoundSkin } from './skins/nightRound';
import { yardageBookSkin } from './skins/yardageBook';
import { readStoredSkinName } from './skinStorage';

/**
 * The shape every skin must fill: the classic skin defines it, TypeScript holds the others to
 * it. A skin is chosen once, when this module loads, and the UI never switches skins while
 * running, because most style sheets are built at module load. Changing the choice on a device
 * takes effect at the next start.
 */
export type Skin = typeof classicSkin;

export const DEFAULT_SKIN = 'classic';

export const skins: Record<string, Skin> = {
  classic: classicSkin,
  'night-round': nightRoundSkin,
  'yardage-book': yardageBookSkin,
  clubhouse: clubhouseSkin,
};

/** The device's stored choice wins over the build's EXPO_PUBLIC_SKIN. */
export function chooseSkinName(stored: string | undefined, buildDefault: string | undefined): string | undefined {
  return stored ?? buildDefault;
}

/** The skin for a name; an unknown or missing name means the default. */
export function resolveSkin(name: string | undefined): Skin {
  if (name && skins[name]) return skins[name];
  if (name) console.warn(`Unknown skin "${name}", using "${DEFAULT_SKIN}".`);
  return skins[DEFAULT_SKIN];
}

// A plain member expression: babel-preset-expo only inlines EXPO_PUBLIC_* read this way.
const requestedName = chooseSkinName(readStoredSkinName(), process.env.EXPO_PUBLIC_SKIN);

export const activeSkin: Skin = resolveSkin(requestedName);

/** The registered name of the active skin, for the picker to mark the current choice. */
export const activeSkinName: string =
  Object.keys(skins).find((name) => skins[name] === activeSkin) ?? DEFAULT_SKIN;
